import {ApiClient} from '@hconnect/apiclient'
import {Iso8601} from '@hconnect/common/types'
import {spawnErrorToast} from '@hm/fundamental'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {stringify} from 'qs'
import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../../consts'
import {
  DowntimeDashboardQueryKey,
  type StoppageCategoryCounts,
  StoppageSource,
  StoppageType
} from '../../types'

const QueryPrefix = DowntimeDashboardQueryKey.StoppageCategoryTypesCount

type Params = {
  plantId: string
  from: Iso8601
  to: Iso8601
  stoppageSources?: StoppageSource[]
  stoppageTypes?: StoppageType[]
  pxTrendTags?: string[]
}

export const useStoppagesCategoryTypeCounts = (
  params: Params,
  options?: Omit<UseQueryOptions<StoppageCategoryCounts[], AxiosError>, 'queryKey' | 'queryFn'>
) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)
  const {plantId, from, to, stoppageSources, stoppageTypes, pxTrendTags} = params

  const query = useQuery<StoppageCategoryCounts[], AxiosError>({
    queryKey: [QueryPrefix, plantId, from, to, stoppageSources, stoppageTypes, pxTrendTags],
    queryFn: async ({signal}) => {
      const path = `/downtime-dashboard/${plantId}/stoppages/category-types-count`
      const response = await ApiClient.axiosInstance.get<StoppageCategoryCounts[]>(path, {
        params: {
          from,
          to,
          stoppageSources,
          stoppageTypes,
          pxTrendTags
        },
        paramsSerializer: (params) => stringify(params, {arrayFormat: 'repeat'}),
        signal
      })

      return response.data
    },
    ...options
  })

  useEffect(() => {
    if (query.error) {
      spawnErrorToast(t('message.failedToFetchDowntimeByCategory'), {closeOnClick: true})
    }
  }, [query.error, t])

  return query
}
